import { useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';

import {
  BuildingEnergySettingReadModel,
  PermissionKeys,
  PriceSourceType,
  useGetBuildingsByBuildingIdEnergySettingsQuery,
  usePutBuildingsByBuildingIdEnergySettingsMutation,
  useGetBuildingsByBuildingIdBidAreaQuery,
  useGetBuildingsByBuildingIdAvailableBidAreasQuery,
  usePutBuildingsByBuildingIdBidAreaMutation,
  useGetBuildingsByIdQuery,
} from '@client/api';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyPlaceholder,
  Input,
  isDefinedString,
  PageDetailContent,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  TableRow,
  toUTCDate,
  toZonedDateStringOrUndefined,
} from '@client/toolkit';

import { PageContentAnimation } from '../../../../../components/layout/page-content-animation';
import { useSpacePermissions } from '../../../../../hooks/useSpacePermissions/useSpacePermissions';
import { formatHeatingGrade } from '../../heating-grade.util';

const PLACEHOLDER = '\u2013';

interface YearData {
  priceSource: PriceSourceType | '';
  customPrice: string;
  consumption: string;
}

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 4 }, (_, i) => currentYear - 3 + i);

function createDefaultYearData(): Record<number, YearData> {
  const data: Record<number, YearData> = {};
  YEARS.forEach((year) => {
    data[year] = { priceSource: '', customPrice: '', consumption: '' };
  });
  return data;
}

function mapServerDataToLocal(settings: BuildingEnergySettingReadModel[]): Record<number, YearData> {
  const data = createDefaultYearData();
  settings.forEach((s) => {
    data[s.year] = {
      priceSource: s.priceSource,
      customPrice: s.customPricePerKwh != null ? String(s.customPricePerKwh) : '',
      consumption: s.consumptionKwh != null ? String(s.consumptionKwh) : '',
    };
  });
  return data;
}

export function BuildingEnergyRoute() {
  const { t: tCommon } = useTranslation();
  const { t: tEos } = useTranslation('eos');
  const { t: tGlobal } = useTranslation('global');
  const { t: tNotification } = useTranslation('notification');
  const { buildingId } = useParams<{ buildingId: string }>();

  const { data: building } = useGetBuildingsByIdQuery({ id: buildingId ?? '' }, { skip: !buildingId });
  const certificateIssuedDate = isDefinedString(building?.energyCertificateIssuedDate)
    ? toZonedDateStringOrUndefined(toUTCDate(building.energyCertificateIssuedDate))
    : undefined;
  const hasCertificate =
    isDefinedString(building?.energyCertificateUrl) ||
    isDefinedString(building?.energyLabel) ||
    isDefinedString(building?.heatingGrade) ||
    certificateIssuedDate != null;
  const {
    hasAnyPermission,
    isLoading: isLoadingPermissions,
    isError: isPermissionsError,
  } = useSpacePermissions({ id: buildingId });
  const canEdit = isLoadingPermissions || isPermissionsError || hasAnyPermission([PermissionKeys.Space.Update]);

  const { data: serverSettings, isFetching } = useGetBuildingsByBuildingIdEnergySettingsQuery(
    { buildingId: buildingId ?? '' },
    { skip: !buildingId },
  );
  const [putEnergySetting] = usePutBuildingsByBuildingIdEnergySettingsMutation();

  const { data: currentBidArea, isFetching: isFetchingBidArea } = useGetBuildingsByBuildingIdBidAreaQuery(
    { buildingId: buildingId ?? '' },
    { skip: !buildingId },
  );
  const { data: availableBidAreas } = useGetBuildingsByBuildingIdAvailableBidAreasQuery(
    { buildingId: buildingId ?? '' },
    { skip: !buildingId },
  );
  const [putBidArea] = usePutBuildingsByBuildingIdBidAreaMutation();

  const [editing, setEditing] = useState(false);
  const [selectedBidAreaId, setSelectedBidAreaId] = useState<string>('');

  useEffect(() => {
    if (currentBidArea) {
      setSelectedBidAreaId(currentBidArea.bidAreaId);
    }
  }, [currentBidArea]);
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [yearData, setYearData] = useState<Record<number, YearData>>(createDefaultYearData);

  useEffect(() => {
    if (serverSettings) {
      setYearData(mapServerDataToLocal(serverSettings));
    }
  }, [serverSettings]);

  const currentData = useMemo(() => yearData[selectedYear], [yearData, selectedYear]);

  const updateField = useCallback(
    <K extends keyof YearData>(field: K, value: YearData[K]) => {
      setYearData((prev) => ({
        ...prev,
        [selectedYear]: { ...prev[selectedYear], [field]: value },
      }));
    },
    [selectedYear],
  );

  const handleSave = async () => {
    if (!buildingId) return;

    const yearsToSave = YEARS.filter((year) => yearData[year].priceSource !== '');

    try {
      const promises: Promise<unknown>[] = yearsToSave.map((year) => {
        const data = yearData[year];
        return putEnergySetting({
          buildingId,
          body: {
            year,
            priceSource: data.priceSource as PriceSourceType,
            customPricePerKwh: data.customPrice ? Number(data.customPrice) : null,
            consumptionKwh: data.consumption ? Number(data.consumption) : null,
          },
        }).unwrap();
      });

      if (selectedBidAreaId && selectedBidAreaId !== currentBidArea?.bidAreaId) {
        promises.push(
          putBidArea({
            buildingId,
            body: { bidAreaId: selectedBidAreaId },
          }).unwrap(),
        );
      }

      await Promise.all(promises);
      toast.success(tNotification('editing.fulfilled'));
      setEditing(false);
    } catch {
      // error handled by rtk query middleware
    }
  };

  const handleCancel = () => {
    if (serverSettings) {
      setYearData(mapServerDataToLocal(serverSettings));
    }
    setSelectedBidAreaId(currentBidArea?.bidAreaId ?? '');
    setEditing(false);
  };

  return (
    <>
      <PageDetailContent align="left">
        <PageContentAnimation>
          <Card>
            <CardHeader>
              <CardTitle className="flex-1" variant="dense">
                {tEos('energySettings')}
              </CardTitle>
              <CardActions>
                {!editing ? (
                  canEdit && (
                    <Button color="primary-dark" size="sm" disabled={isFetching} onClick={() => setEditing(true)}>
                      {tCommon('edit')}
                    </Button>
                  )
                ) : (
                  <>
                    <Button color="primary-dark" size="sm" onClick={handleCancel}>
                      {tCommon('cancel')}
                    </Button>
                    <Button color="primary" size="sm" onClick={handleSave}>
                      {tCommon('save')}
                    </Button>
                  </>
                )}
              </CardActions>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-1">
                    <label className="text-sm text-white">{tEos('year')}</label>
                    <Select
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(Number(e.target.value))}
                      disabled={!editing}
                    >
                      {YEARS.map((year) => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </Select>
                  </div>

                  <div className="space-y-1">
                    <label className="text-sm text-white">{tEos('electricityPriceSource')}</label>
                    <Select
                      value={currentData.priceSource}
                      onChange={(e) => updateField('priceSource', e.target.value as PriceSourceType | '')}
                      disabled={!editing}
                    >
                      <option value="">{tEos('selectPriceSource')}</option>
                      <option value="Nordpool">{tEos('nordpool')}</option>
                      <option value="Custom">{tEos('customPrice')}</option>
                    </Select>
                  </div>

                  <div className="space-y-1">
                    <label className="text-sm text-white">{tEos('bidArea')}</label>
                    {editing ? (
                      <Select
                        value={selectedBidAreaId}
                        onChange={(e) => setSelectedBidAreaId(e.target.value)}
                        disabled={!editing}
                      >
                        <option value="">{tEos('selectBidArea')}</option>
                        {availableBidAreas?.map((area) => (
                          <option key={area.id} value={area.id}>
                            {area.bidArea}
                          </option>
                        ))}
                      </Select>
                    ) : (
                      <div className="text-sm text-white/70 py-2">
                        {currentBidArea?.bidArea ?? tEos('noBidAreaSet')}
                      </div>
                    )}
                  </div>

                  {currentData.priceSource === 'Custom' && (
                    <div className="space-y-1">
                      <label className="text-sm text-white">{tEos('customPriceLabel')}</label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={currentData.customPrice}
                        onChange={(e) => updateField('customPrice', e.target.value)}
                        disabled={!editing}
                      />
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-1">
                    <label className="text-sm text-white">{tEos('electricityConsumption')}</label>
                    <Input
                      type="number"
                      step="1"
                      min="0"
                      value={currentData.consumption}
                      onChange={(e) => updateField('consumption', e.target.value)}
                      disabled={!editing}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="mt-4">
            <CardHeader>
              <CardTitle className="flex-1" variant="dense">
                {tGlobal('building.energyCertificate')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Card>
                <Table className="rounded-md overflow-hidden">
                  <TableHead>
                    <TableHeadRow>
                      <TableHeadCell className="rounded-tl-md">{tGlobal('building.attest')}</TableHeadCell>
                      <TableHeadCell>{tGlobal('building.energyLabel')}</TableHeadCell>
                      <TableHeadCell>{tGlobal('building.heatingGrade')}</TableHeadCell>
                      <TableHeadCell className="rounded-tr-md">
                        {tGlobal('building.energyCertificateIssuedDate')}
                      </TableHeadCell>
                    </TableHeadRow>
                  </TableHead>

                  <TableBody>
                    {hasCertificate ? (
                      <TableRow>
                        <TableCell>
                          {isDefinedString(building?.energyCertificateUrl) ? (
                            <a
                              href={building?.energyCertificateUrl ?? undefined}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-brand-primary hover:underline"
                            >
                              {tGlobal('building.energyCertificateView')}
                            </a>
                          ) : (
                            PLACEHOLDER
                          )}
                        </TableCell>
                        <TableCell className="text-brand-primary">
                          {isDefinedString(building?.energyLabel) ? building?.energyLabel : PLACEHOLDER}
                        </TableCell>
                        <TableCell>
                          {isDefinedString(building?.heatingGrade)
                            ? formatHeatingGrade(building?.heatingGrade, tGlobal)
                            : PLACEHOLDER}
                        </TableCell>
                        <TableCell>{certificateIssuedDate ?? PLACEHOLDER}</TableCell>
                      </TableRow>
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} className="p-4">
                          <EmptyPlaceholder fullWidth>{tGlobal('building.noEnergyCertificate')}</EmptyPlaceholder>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Card>
            </CardContent>
          </Card>
        </PageContentAnimation>
      </PageDetailContent>
      <Outlet />
    </>
  );
}
